import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RazorpayProvider } from './contexts/RazorpayContext';
import { ScrollProvider } from './contexts/ScrollContext';
import { useAuth } from './contexts/AuthContext';
import { Helmet } from 'react-helmet';
import './styles/balloon-theme.css'; // New balloon theme stylesheet
import ScrollToTop from './components/ScrollToTop';

// Layout components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Critical pages (immediate load)
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import ShippingAndDelivery from './pages/ShippingAndDelivery';
import CategoryPage from './pages/CategoryPage';
import CategoriesPage from './pages/CategoriesPage';
import EmailTest from './pages/EmailTest'; // Import the EmailTest page

// Lazy loaded pages
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const BookingManagement = lazy(() => import('./pages/admin/BookingManagement'));
const AddOnManagement = lazy(() => import('./pages/admin/AddOnManagement'));
const Menu = lazy(() => import('./pages/Menu'));
const Events = lazy(() => import('./pages/Events'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Contact = lazy(() => import('./pages/Contact'));

// Protected route component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { currentUser, isAdmin } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  if (isAdmin && !requireAdmin && window.location.pathname === '/dashboard') {
    return <Navigate to="/admin" />;
  }
  
  return children;
};

// Page layout wrapper with loading fallback
const PageWrapper = ({ children, setContentLoaded }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setContentLoaded(false);

    // Always scroll to top when a page loads
    window.scrollTo(0, 0);

    // Use a small delay to prevent flash of loading state for fast loads
    const timer = setTimeout(() => {
      setIsLoading(false);
      setContentLoaded(true);
      // Ensure we're at the top of the page
      window.scrollTo(0, 0);
    }, 300);

    return () => {
      clearTimeout(timer);
      setContentLoaded(false);
      setIsLoading(true);
    };
  }, [setContentLoaded]);

  if (isLoading) {
    return (
      <div className="flex-grow flex items-center justify-center min-h-[calc(100vh-5rem)] fixed inset-0 bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="balloon-loader">
          <div className="balloon balloon-1"></div>
          <div className="balloon balloon-2"></div>
          <div className="balloon balloon-3"></div>
          <div className="balloon balloon-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow  ">
      <Suspense fallback={
        <div className="flex-grow flex items-center justify-center min-h-100vh h-screen fixed inset-0 ">
          <div className="balloon-loader">
            <div className="balloon balloon-1"></div>
            <div className="balloon balloon-2"></div>
            <div className="balloon balloon-3"></div>
            <div className="balloon balloon-4"></div>
          </div>
        </div>
      }>
        {children}
      </Suspense>
    </div>
  );
};

function App() {
  const [contentLoaded, setContentLoaded] = useState(false);

  return (
    <Router>
      <ScrollProvider>
        <AuthProvider>
          <RazorpayProvider>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen bg-gradient-to-r from-pink-50 to-purple-50">
              <Helmet>
                <title>Habba Events - Premium Balloon Decorations & Party Services</title>
                <meta name="description" content="Transform your special occasions with Habba Events premium balloon decorations. We offer customized decoration services for birthdays, anniversaries, baby showers, and corporate events." />
                <meta name="keywords" content="balloon decoration, party decor, event decoration, birthday decoration, anniversary decoration, baby shower decoration, balloon bouquets, themed party, event planning, same day decoration" />
                <meta property="og:title" content="Habba Events - Premium Balloon Decorations & Party Services" />
                <meta property="og:description" content="Transform any space into a celebration zone with our stunning balloon decorations for birthdays, anniversaries, baby showers and corporate events." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://habbaevents.com" />
                <meta property="og:image" content="/icon.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Habba Events - Premium Balloon Decorations & Party Services" />
                <meta name="twitter:description" content="Transform any space into a celebration zone with our stunning balloon decorations for birthdays, anniversaries, baby showers and corporate events." />
                <link rel="canonical" href="https://habbaevents.com" />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="theme-color" content="#FF6EC7" />
                <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Dancing+Script:wght@700&display=swap" rel="stylesheet" />
              </Helmet>
              <Navbar />
              <main className="flex-grow flex flex-col">
                <Routes>
                  <Route path="/" element={<PageWrapper setContentLoaded={setContentLoaded}><Home /></PageWrapper>} />
                  <Route path="/menu" element={<PageWrapper setContentLoaded={setContentLoaded}><Menu /></PageWrapper>} />
                  <Route path="/events" element={<PageWrapper setContentLoaded={setContentLoaded}><Events /></PageWrapper>} />
                  <Route path="/gallery" element={<PageWrapper setContentLoaded={setContentLoaded}><Gallery /></PageWrapper>} />
                  <Route path="/about" element={<PageWrapper setContentLoaded={setContentLoaded}><About /></PageWrapper>} />
                  <Route path="/contact" element={<PageWrapper setContentLoaded={setContentLoaded}><Contact /></PageWrapper>} />
                  <Route path="/privacy-policy" element={<PageWrapper setContentLoaded={setContentLoaded}><PrivacyPolicy /></PageWrapper>} />
                  <Route path="/terms-and-conditions" element={<PageWrapper setContentLoaded={setContentLoaded}><TermsAndConditions /></PageWrapper>} />
                  <Route path="/refund-policy" element={<PageWrapper setContentLoaded={setContentLoaded}><RefundPolicy /></PageWrapper>} />
                  <Route path="/shipping-and-delivery" element={<PageWrapper setContentLoaded={setContentLoaded}><ShippingAndDelivery /></PageWrapper>} />
                  <Route path="/login" element={<PageWrapper setContentLoaded={setContentLoaded}><Login /></PageWrapper>} />
                  <Route path="/signup" element={<PageWrapper setContentLoaded={setContentLoaded}><Signup /></PageWrapper>} />
                  <Route path="/booking-confirmation/:bookingId" element={<PageWrapper setContentLoaded={setContentLoaded}><BookingConfirmation /></PageWrapper>} />
                  <Route path="/booking/:serviceId" element={<PageWrapper setContentLoaded={setContentLoaded}><BookingPage /></PageWrapper>} />
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <PageWrapper setContentLoaded={setContentLoaded}><UserDashboard /></PageWrapper>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <PageWrapper setContentLoaded={setContentLoaded}><AdminDashboard /></PageWrapper>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/bookings" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <PageWrapper setContentLoaded={setContentLoaded}><BookingManagement /></PageWrapper>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/add-ons" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <PageWrapper setContentLoaded={setContentLoaded}><AddOnManagement /></PageWrapper>
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/category/:categorySlug" element={<PageWrapper setContentLoaded={setContentLoaded}><CategoryPage /></PageWrapper>} />
                  <Route path="/category/:categorySlug/:subcategorySlug" element={<PageWrapper setContentLoaded={setContentLoaded}><CategoryPage /></PageWrapper>} />
                  <Route path="/categories" element={<PageWrapper setContentLoaded={setContentLoaded}><CategoriesPage /></PageWrapper>} />
                  <Route path="/email-test" element={<PageWrapper setContentLoaded={setContentLoaded}><EmailTest /></PageWrapper>} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </main>
              {contentLoaded && <Footer />}
            </div>
          </RazorpayProvider>
        </AuthProvider>
      </ScrollProvider>
    </Router>
  );
}

export default App;
